import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import BlogArticleListFilters from '../../Components/BlogArticleAdmin/BlogArticleListFilters';
import BlogArticleListPaging, { ROWS_PER_PAGE_OPTIONS } from '../../Components/BlogArticleAdmin/BlogArticleListPaging';
import BlogArticleListRow from '../../Components/BlogArticleAdmin/BlogArticleListRow';
import { changeBlogArticleStatus, fetchBlogArticles } from '../../Util/ApiService';
import { displayToast } from '../../Util/Toast';
const ROWS_PER_PAGE_STORAGE_KEY = 'admin.blogArticles.rowsPerPage';
const DEFAULT_ROWS_PER_PAGE = 25;
const DEFAULT_ORDER_BY = 'updated';
const DEFAULT_ORDER_DIRECTION = 'DESC';
const FIRST_SORT_DIRECTION = {
    id: 'DESC',
    title: 'ASC',
    author: 'ASC',
    category: 'ASC',
    status: 'ASC',
    updated: 'DESC'
};
const EMPTY_FILTERS = {
    title: '',
    author: '',
    site: '',
    blogArticleStatus: '',
    blogCategory: ''
};
const FILTER_KEYS = Object.keys(EMPTY_FILTERS);
const DELETE_CONFIRMATION = 'Delete this blog article? It will be removed from the site but kept in the admin.';
const COLUMNS = [
    { label: 'ID', width: 'w-16', sortKey: 'id' },
    { label: 'Article', width: '', sortKey: 'title' },
    { label: 'Author', width: 'w-32', sortKey: 'author' },
    { label: 'Category', width: 'w-32', sortKey: 'category' },
    { label: 'Sites', width: 'w-52' },
    { label: 'Status', width: 'w-32', sortKey: 'status' },
    { label: 'Last updated', width: 'w-36', sortKey: 'updated' },
    { label: 'Actions', width: 'w-[14.5rem]' }
];
const readStoredRowsPerPage = () => {
    try {
        const stored = Number(window.localStorage.getItem(ROWS_PER_PAGE_STORAGE_KEY));
        return ROWS_PER_PAGE_OPTIONS.includes(stored)
            ? stored
            : DEFAULT_ROWS_PER_PAGE;
    }
    catch (_a) {
        return DEFAULT_ROWS_PER_PAGE;
    }
};
const storeRowsPerPage = (length) => {
    try {
        window.localStorage.setItem(ROWS_PER_PAGE_STORAGE_KEY, String(length));
    }
    catch (_a) {
        // storage can be blocked, the choice then lasts for this visit only
    }
};
const readQueryFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    const orderBy = params.get('orderBy');
    const page = Number(params.get('page'));
    const filters = Object.assign({}, EMPTY_FILTERS);
    FILTER_KEYS.forEach((key) => {
        var _a;
        filters[key] = (_a = params.get(key)) !== null && _a !== void 0 ? _a : '';
    });
    return Object.assign(Object.assign({}, filters), { orderBy: orderBy in FIRST_SORT_DIRECTION ? orderBy : DEFAULT_ORDER_BY, orderDirection: params.get('orderDirection') === 'ASC' ? 'ASC' : 'DESC', page: Number.isInteger(page) && page > 1 ? page : 1, length: readStoredRowsPerPage() });
};
const writeQueryToUrl = (query) => {
    const params = new URLSearchParams();
    FILTER_KEYS.forEach((key) => {
        if (query[key]) {
            params.set(key, query[key]);
        }
    });
    if (query.orderBy !== DEFAULT_ORDER_BY ||
        query.orderDirection !== DEFAULT_ORDER_DIRECTION) {
        params.set('orderBy', query.orderBy);
        params.set('orderDirection', query.orderDirection);
    }
    if (query.page > 1) {
        params.set('page', String(query.page));
    }
    const search = params.toString();
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${search ? `?${search}` : ''}`);
};
const buildRequestParams = (query, draw) => {
    const params = {
        orderBy: query.orderBy,
        orderDirection: query.orderDirection,
        start: (query.page - 1) * query.length,
        length: query.length,
        draw
    };
    FILTER_KEYS.forEach((key) => {
        if (query[key]) {
            params[key] = query[key];
        }
    });
    return params;
};
const withId = (uriTemplate, id) => {
    return uriTemplate.replace('__id__', String(id));
};
const errorMessage = (error) => {
    var _a;
    const data = (_a = error.response) === null || _a === void 0 ? void 0 : _a.data;
    return typeof data === 'string' && data !== ''
        ? data
        : 'Could not update the blog article status.';
};
const BlogArticlesAdmin = ({ uris, sites, statuses, categories }) => {
    var _a, _b;
    const [query, setQuery] = useState(readQueryFromUrl);
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const [busyArticleId, setBusyArticleId] = useState(null);
    const [reloadCount, setReloadCount] = useState(0);
    const latestRequest = useRef(0);
    const reload = useCallback(() => {
        setReloadCount((count) => {
            return count + 1;
        });
    }, []);
    useEffect(() => {
        writeQueryToUrl(query);
    }, [query]);
    useEffect(() => {
        latestRequest.current += 1;
        const draw = latestRequest.current;
        setLoading(true);
        fetchBlogArticles(uris.list, buildRequestParams(query, draw)).then((response) => {
            if (draw !== latestRequest.current) {
                return;
            }
            setLoading(false);
            setFailed(!response);
            if (!response) {
                return;
            }
            setResult(response);
            const lastPage = Math.max(1, Math.ceil(response.recordsFiltered / query.length));
            if (query.page > lastPage) {
                setQuery(Object.assign(Object.assign({}, query), { page: lastPage }));
            }
        });
    }, [query, reloadCount, uris.list]);
    useEffect(() => {
        window.blogArticleScheduleSaved = reload;
        return () => {
            delete window.blogArticleScheduleSaved;
        };
    }, [reload]);
    const filters = useMemo(() => {
        return {
            title: query.title,
            author: query.author,
            site: query.site,
            blogArticleStatus: query.blogArticleStatus,
            blogCategory: query.blogCategory
        };
    }, [
        query.title,
        query.author,
        query.site,
        query.blogArticleStatus,
        query.blogCategory
    ]);
    const hasFilters = FILTER_KEYS.some((key) => {
        return query[key] !== '';
    });
    const sitesSortedByCode = useMemo(() => {
        return [...sites].sort((first, second) => {
            return first.siteCode.localeCompare(second.siteCode);
        });
    }, [sites]);
    const sitesByCode = useMemo(() => {
        return new Map(sites.map((site) => {
            return [site.siteCode, site];
        }));
    }, [sites]);
    const buildSiteUrl = (row, siteCode) => {
        var _a, _b;
        const site = sitesByCode.get(siteCode);
        const slug = site
            ? (_b = (_a = row.translations[site.languageCode]) === null || _a === void 0 ? void 0 : _a.urlSlug) !== null && _b !== void 0 ? _b : row.urlSlug
            : null;
        if (!site || !row.blogCategory || !slug) {
            return null;
        }
        return (site.baseUri +
            uris.articleOnSite
                .replace('__categorySlug__', encodeURIComponent(row.blogCategory.slug))
                .replace('__articleSlug__', encodeURIComponent(slug)));
    };
    const isOnEverySite = (row) => {
        return sites.every((site) => {
            return row.sitesCodes.includes(site.siteCode);
        });
    };
    const changeStatus = (uriTemplate, row) => {
        setBusyArticleId(row.blogArticleId);
        changeBlogArticleStatus(withId(uriTemplate, row.blogArticleId), reload, (error) => {
            displayToast(errorMessage(error), 'error');
        }).then(() => {
            setBusyArticleId(null);
        });
    };
    const actions = {
        edit: (row) => {
            var _a;
            (_a = window.openBlogArticleModal) === null || _a === void 0 ? void 0 : _a.call(window, {
                mode: 'edit',
                article: row,
                updateUri: withId(uris.edit, row.blogArticleId),
                updateTranslationUri: withId(uris.translation, row.blogArticleId),
                translations: row.translations || {},
                imageUrl: row.imageMetadataUuid
                    ? withId(uris.image, row.imageMetadataUuid)
                    : null,
                imageAltText: row.imageAltText || ''
            });
        },
        publish: (row) => {
            changeStatus(uris.publish, row);
        },
        pause: (row) => {
            changeStatus(uris.pause, row);
        },
        schedule: (row) => {
            var _a;
            (_a = window.openBlogArticleScheduleModal) === null || _a === void 0 ? void 0 : _a.call(window, row.blogArticleId, row.scheduledPublishAt);
        },
        cancelSchedule: (row) => {
            changeStatus(uris.cancelSchedule, row);
        },
        remove: (row) => {
            if (window.confirm(DELETE_CONFIRMATION)) {
                changeStatus(uris.delete, row);
            }
        }
    };
    const sortBy = (sortKey) => {
        const toggledDirection = query.orderDirection === 'ASC' ? 'DESC' : 'ASC';
        setQuery(Object.assign(Object.assign({}, query), { orderBy: sortKey, orderDirection: query.orderBy === sortKey
                ? toggledDirection
                : FIRST_SORT_DIRECTION[sortKey], page: 1 }));
    };
    const changePage = (page) => {
        setQuery(Object.assign(Object.assign({}, query), { page }));
    };
    const changeRowsPerPage = (length) => {
        storeRowsPerPage(length);
        setQuery(Object.assign(Object.assign({}, query), { length, page: 1 }));
    };
    const clearFilters = () => {
        setQuery(Object.assign(Object.assign(Object.assign({}, query), EMPTY_FILTERS), { page: 1 }));
    };
    const rows = (_a = result === null || result === void 0 ? void 0 : result.data) !== null && _a !== void 0 ? _a : [];
    const articleNoun = (result === null || result === void 0 ? void 0 : result.recordsTotal) === 1 ? 'article' : 'articles';
    const articleCount = result
        ? `${result.recordsTotal} ${articleNoun}`
        : '\u00a0';
    const recordsFiltered = (_b = result === null || result === void 0 ? void 0 : result.recordsFiltered) !== null && _b !== void 0 ? _b : 0;
    let emptyMessage = null;
    if (failed) {
        emptyMessage = 'The articles could not be loaded. Please try again.';
    }
    else if (!result) {
        emptyMessage = 'Loading articles…';
    }
    else if (rows.length === 0) {
        emptyMessage = hasFilters
            ? 'No articles match these filters.'
            : 'There are no blog articles yet.';
    }
    return (React.createElement("div", { className: "pb-8 text-sm text-gray-800" },
        React.createElement("div", { className: "flex flex-wrap items-center justify-between gap-4 pt-6 pb-4" },
            React.createElement("div", null,
                React.createElement("h1", { className: "text-2xl font-semibold text-gray-900" }, "Blog articles"),
                React.createElement("p", { className: "mt-0.5 text-sm text-gray-500" }, articleCount)),
            React.createElement("button", { type: "button", onClick: () => {
                    var _a;
                    (_a = window.openBlogArticleModal) === null || _a === void 0 ? void 0 : _a.call(window, { mode: 'add' });
                }, className: "inline-flex h-10 items-center gap-1.5 rounded-md bg-blue-600 pr-4 pl-3 text-sm font-semibold text-white hover:bg-blue-700" },
                React.createElement("span", { className: "material-symbols-outlined text-xl", "aria-hidden": true }, "add"),
                "Add blog article")),
        React.createElement(BlogArticleListFilters, { filters: filters, sites: sitesSortedByCode, statuses: statuses, categories: categories, onApply: (appliedFilters) => {
                setQuery(Object.assign(Object.assign(Object.assign({}, query), appliedFilters), { page: 1 }));
            }, onClear: clearFilters }),
        React.createElement(BlogArticleListPaging, { page: query.page, length: query.length, recordsFiltered: recordsFiltered, onPageChange: changePage, onLengthChange: changeRowsPerPage }),
        React.createElement("div", { className: "max-h-[calc(100vh-10rem)] overflow-auto rounded-lg border border-gray-200 bg-white" },
            React.createElement("table", { "aria-label": "Blog articles", className: "w-full min-w-[80rem] table-fixed border-collapse text-left" },
                React.createElement("colgroup", null, COLUMNS.map((column) => {
                    return React.createElement("col", { key: column.label, className: column.width });
                })),
                React.createElement("thead", null,
                    React.createElement("tr", null, COLUMNS.map((column) => {
                        const isSorted = column.sortKey === query.orderBy;
                        const ascending = query.orderDirection === 'ASC';
                        let ariaSort;
                        if (isSorted) {
                            ariaSort = ascending ? 'ascending' : 'descending';
                        }
                        return (React.createElement("th", { key: column.label, scope: "col", "aria-sort": ariaSort, className: "sticky top-0 z-10 bg-gray-50 px-3 py-2.5 text-xs font-semibold tracking-wide whitespace-nowrap text-gray-500 uppercase shadow-[inset_0_-1px_0_var(--color-gray-200)]" }, column.sortKey ? (React.createElement("button", { type: "button", onClick: () => {
                                sortBy(column.sortKey);
                            }, className: `group inline-flex items-center gap-1 uppercase hover:text-gray-900 ${isSorted ? 'text-gray-900' : ''}` },
                            column.label,
                            React.createElement("span", { className: `material-symbols-outlined text-base ${isSorted
                                    ? ''
                                    : 'opacity-0 group-hover:opacity-60 group-focus-visible:opacity-60'}`, "aria-hidden": true }, isSorted && ascending
                                ? 'arrow_upward'
                                : 'arrow_downward'))) : (column.label)));
                    }))),
                React.createElement("tbody", { "aria-busy": loading, className: loading && result ? 'opacity-60' : '' }, emptyMessage ? (React.createElement("tr", null,
                    React.createElement("td", { colSpan: COLUMNS.length, className: "px-3 py-10 text-center text-gray-500" },
                        emptyMessage,
                        (failed || (hasFilters && !loading)) && (React.createElement("button", { type: "button", onClick: failed ? reload : clearFilters, className: "ml-2 font-semibold text-blue-700 hover:underline" }, failed ? 'Retry' : 'Clear filters'))))) : (rows.map((row) => {
                    return (React.createElement(BlogArticleListRow, { key: row.blogArticleId, row: row, articleAdminUri: withId(uris.articleAdmin, row.blogArticleId), isOnEverySite: isOnEverySite(row), buildSiteUrl: buildSiteUrl, actions: actions, busy: busyArticleId === row.blogArticleId }));
                }))))),
        React.createElement(BlogArticleListPaging, { page: query.page, length: query.length, recordsFiltered: recordsFiltered, onPageChange: changePage })));
};
export default BlogArticlesAdmin;
